import { TextButton } from '@creditornot/cb-button';
import { ArrowLeft } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import React, { useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import { useHistory, useRouteMatch, Switch, Route, Redirect } from 'react-router-dom';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';
import Loading from 'core/Loading';
import { useListingManagerImageGalleryFeatureFlag } from 'modules/shared-menu/hooks/useListingManagerImageGalleryFeatureFlag';
import { useListingManagerRoutes } from 'modules/shared-menu/hooks/useListingManagerRoutes';

import { ImageGalleryNavigation } from './components/ImageGalleryNavigation/ImageGalleryNavigation';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const HeaderContainer = styled.div`
  display: flex;
  padding: 44px 24px 16px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  align-items: flex-start;
  flex-direction: column;
  justify-content: flex-end;
  gap: 16px;
`;

const Header = styled(T.Heading4)`
  display: flex;
  flex-direction: column;
`;

const StyledTextButton = styled(TextButton)`
  font-weight: bold;
  margin-block-end: 8px;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex: 1;
  overflow: auto;
`;

const NavigationMenu = styled.div`
  padding: 12px;
  width: max-content;
  min-width: 200px;
  flex: 0 1;
  border-inline-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

export const Main = styled.div`
  flex: 1;
  overflow: auto;
`;

export const ListingManagerImageGalleryPage = () => {
  const history = useHistory();
  const match = useRouteMatch();
  const { rootPage } = useListingManagerRoutes();
  const { menuEditorPage } = useListingManagerRoutes();
  const mainElementRef = useRef<HTMLDivElement>(null);
  const { isLoading, isListingManagerImageGalleryEnabled } = useListingManagerImageGalleryFeatureFlag();

  const goBackToMenuEditor = useCallback(() => {
    toast.dismiss();
    history.push(menuEditorPage());
  }, [history, menuEditorPage]);

  if (isLoading) {
    return <Loading />;
  }

  return isListingManagerImageGalleryEnabled ? (
    <Root>
      <HeaderContainer>
        <Header>
          <StyledTextButton variant="black" size="small" icon={<ArrowLeft />} onClick={goBackToMenuEditor}>
            <FormattedMessage id="shared-menu.editor.back-button" />
          </StyledTextButton>
          <FormattedMessage id="shared-menu.editor.image-gallery.title" />
        </Header>
      </HeaderContainer>
      <ContentWrapper>
        <NavigationMenu>
          <ImageGalleryNavigation />
        </NavigationMenu>
        <Switch>
          <Route path={`${match.path}`} exact key="browse-images">
            <Main ref={mainElementRef}>
              <h1>
                <FormattedMessage id="shared-menu.editor.image-gallery.menu.browse-images" />
              </h1>
            </Main>
          </Route>
          <Route path={`${match.path}/edit-images`} exact key="edit-images">
            <Main ref={mainElementRef}>
              <h1>
                <FormattedMessage id="shared-menu.editor.image-gallery.menu.edit-images" />
              </h1>
            </Main>
          </Route>
          <Route path={`${match.path}/add-to-items`} exact key="add-to-items">
            <Main ref={mainElementRef}>
              <h1>
                <FormattedMessage id="shared-menu.editor.image-gallery.menu.add-to-items" />
              </h1>
            </Main>
          </Route>
        </Switch>
      </ContentWrapper>
    </Root>
  ) : (
    <Redirect to={rootPage()} />
  );
};
